import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { BASE_URL } from '../lib/api';

export function AcceptInvite() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<'signin' | 'signup'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [entityName, setEntityName] = useState('');

  // Pre-fetch invite preview to show org context and pre-fill email
  useEffect(() => {
    if (!inviteId) return;
    fetch(`${BASE_URL}/invites/${inviteId}/preview`)
      .then(r => r.ok ? r.json() : null)
      .then((data: { data?: { email?: string; entityName?: string } } | null) => {
        if (data?.data) {
          const ie = data.data.email ?? '';
          setInviteEmail(ie);
          setEntityName(data.data.entityName ?? '');
          if (ie) setEmail(ie);
        }
      })
      .catch(() => {});
  }, [inviteId]);

  // Once signed in, call the Cloud Function to set claims, then force-refresh token
  useEffect(() => {
    if (!authLoading && user && inviteId && !accepted && !acceptingInvite && !error) {
      setAcceptingInvite(true);

      const functions = getFunctions(app);
      const setClaimsOnInviteAccept = httpsCallable<
        { inviteId: string },
        { entityId: string; role: string }
      >(functions, 'setClaimsOnInviteAccept');

      setClaimsOnInviteAccept({ inviteId })
        .then(() => user.getIdToken(true))
        .then(() => {
          setAccepted(true);
          setTimeout(() => navigate('/'), 1500);
        })
        .catch((err: unknown) => {
          const code = (err as { code?: string }).code ?? '';
          let message = 'Failed to accept invite. Please try again.';
          if (code === 'functions/not-found') {
            message = 'Invite not found or already used. Check the link in your email.';
          } else if (code === 'functions/already-exists') {
            message = 'This invite has already been accepted.';
          } else if (code === 'functions/failed-precondition') {
            message = 'Your account is already associated with a different organization.';
          } else if (code === 'functions/unauthenticated') {
            message = 'You must be signed in to accept an invite.';
          } else if ((err as { message?: string }).message) {
            message = (err as { message: string }).message;
          }
          setError(message);
          setAcceptingInvite(false);
        });
    }
  }, [user, authLoading, inviteId, accepted, acceptingInvite, navigate, error]);

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return 'Password must be at least 8 characters.';
    if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter.';
    if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must contain a number.';
    return null;
  };

  if (authLoading || acceptingInvite) {
    return (
      <div className="min-h-screen bg-bg-deep flex items-center justify-center">
        <Loader2 size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-bg-deep flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
          <h2 className="text-title font-semibold text-white mb-1">Invite accepted!</h2>
          <p className="text-gray-400 text-meta">Redirecting to your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode === 'signup') {
      const validationError = validatePassword(password);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Incorrect email or password.');
      } else if (code === 'auth/email-already-in-use') {
        setError('Email already registered. Sign in instead.');
        setMode('signin');
      } else if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        setError('Password must be at least 8 characters with uppercase, lowercase, and a number.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-deep flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md bg-bg-surface border border-white/10 rounded-2xl p-8 shadow-2xl"
      >
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold font-display text-white">You've been invited</h1>
          <p className="text-meta text-gray-400 mt-1">
            {entityName ? `Join ${entityName} on OctoPM` : 'Sign in or create an account to accept your invitation.'}
          </p>
          {inviteEmail && (
            <p className="text-micro text-gray-500 mt-1">
              Invite sent to <span className="text-gray-300">{inviteEmail}</span>
            </p>
          )}
        </div>

        {/* Google */}
        <button
          onClick={handleGoogle}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-white/10 rounded-lg text-meta font-medium text-white hover:bg-white/5 transition-colors mb-4 disabled:opacity-50"
        >
          <img src="/integrations/google.svg" alt="" className="h-5 w-5 object-contain" />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-4">
          <hr className="flex-1 border-white/10" />
          <span className="text-micro text-gray-500">or</span>
          <hr className="flex-1 border-white/10" />
        </div>

        <div className="flex gap-1 mb-4 bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setMode('signin')}
            className={`flex-1 py-1.5 text-meta rounded-md font-medium transition-colors ${mode === 'signin' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Sign In
          </button>
          <button
            onClick={() => setMode('signup')}
            className={`flex-1 py-1.5 text-meta rounded-md font-medium transition-colors ${mode === 'signup' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Create Account
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-meta font-medium text-gray-400 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 text-white placeholder-gray-600 text-meta focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
            />
          </div>
          <div>
            <label className="block text-meta font-medium text-gray-400 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={mode === 'signup' ? 'Min 8 chars, upper, lower, number' : '••••••••'}
              required
              className="w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 text-white placeholder-gray-600 text-meta focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-900/20 border border-red-900/30 text-red-400 text-meta">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold text-meta transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === 'signin' ? 'Sign In & Accept' : 'Create Account & Accept'}
          </button>
        </form>

        <p className="text-center text-micro text-gray-600 mt-4">
          <Link to="/login" className="hover:text-gray-400 transition-colors">Back to login</Link>
        </p>
      </motion.div>
    </div>
  );
}
